import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { identifyAnalyticsUser, resetAnalyticsUser } from '../services/posthogService';
import { authService } from '../services/authService';
import { emailVerificationUtils } from '../utils/pendingProject';
const AuthContext = createContext(undefined);
export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    // Initialize auth state on mount
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                setIsLoading(true);
                // Check if we have tokens
                const sessionId = localStorage.getItem('landscope_session_id');
                const sessionToken = localStorage.getItem('landscope_session_token');
                if (sessionId && sessionToken) {
                    try {
                        // Try to get current user to validate the session
                        const userData = await authService.getCurrentUser();
                        const isFullAccount = userData.loginMethod !== 'unverified';
                        setIsAuthenticated(isFullAccount);
                        setUser(userData);
                        // Identify user in PostHog
                        if (isFullAccount && userData.userId) {
                            identifyAnalyticsUser(userData.userId, {
                                email: userData.email,
                            });
                        }
                        // Clear email verification pending status since user is now authenticated
                        if (isFullAccount && emailVerificationUtils.isPending()) {
                            emailVerificationUtils.clear();
                        }
                    }
                    catch (err) {
                        console.error('AuthProvider: Session validation failed, clearing tokens:', err);
                        // Set flag to indicate tokens were cleared due to validation failure
                        localStorage.setItem('landscope_session_id_cleared', 'true');
                        // Clear tokens and set unauthenticated state
                        localStorage.removeItem('landscope_session_id');
                        localStorage.removeItem('landscope_session_token');
                        // Clear cached authenticated client
                        authService.clearAuthenticatedClient();
                        // Clear any cached authenticated clients
                        try {
                            const { artifactExportService } = await import('../services/artifactExportService');
                            artifactExportService.clearClient();
                        }
                        catch (error) {
                            console.error('Failed to clear artifact export client:', error);
                        }
                        setIsAuthenticated(false);
                        setUser(null);
                    }
                }
                else {
                    setIsAuthenticated(false);
                    setUser(null);
                }
            }
            catch (err) {
                console.error('AuthProvider: Failed to initialize auth:', err);
                // Check if we had tokens before clearing them
                const hadTokens = localStorage.getItem('landscope_session_id') && localStorage.getItem('landscope_session_token');
                if (hadTokens) {
                    localStorage.setItem('landscope_session_id_cleared', 'true');
                }
                // Clear tokens on any initialization error
                localStorage.removeItem('landscope_session_id');
                localStorage.removeItem('landscope_session_token');
                // Clear cached authenticated client
                authService.clearAuthenticatedClient();
                // Clear any cached authenticated clients
                try {
                    const { artifactExportService } = await import('../services/artifactExportService');
                    artifactExportService.clearClient();
                }
                catch (error) {
                    console.error('Failed to clear artifact export client:', error);
                }
                setIsAuthenticated(false);
                setUser(null);
                setError('Failed to initialize authentication');
            }
            finally {
                setIsLoading(false);
            }
        };
        initializeAuth();
    }, []);
    const login = useCallback(async (credentials) => {
        try {
            setIsLoading(true);
            setError(null);
            const result = await authService.login(credentials);
            if (result.success) {
                setIsAuthenticated(true);
                setError(null);
                // Clear email verification pending status since user successfully logged in
                if (emailVerificationUtils.isPending()) {
                    emailVerificationUtils.clear();
                }
                // Try to load user data after successful login
                try {
                    const userData = await authService.getCurrentUser();
                    setUser(userData);
                    // Identify user in PostHog
                    if (userData.userId) {
                        identifyAnalyticsUser(userData.userId, {
                            email: userData.email,
                        });
                    }
                }
                catch (err) {
                    console.error('AuthContext: Failed to load user data after login:', err);
                    // Don't throw here - login was successful, just user data loading failed
                }
                // After successful login, check for pending bundle claim
                // Note: This won't trigger immediate redirect - the calling component
                // (like LoginModal in ClaimBundlePage) will handle the redirect via handleLoginSuccess
            }
            else {
                console.error('AuthContext: Login failed with message:', result.message);
                throw new Error(result.message);
            }
        }
        catch (err) {
            console.error('AuthContext: Login error caught:', err);
            const errorMessage = err instanceof Error ? err.message : 'Login failed';
            setError(errorMessage);
            throw err; // Re-throw so calling component can handle it
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const verify = useCallback(async (credentials) => {
        try {
            setIsLoading(true);
            setError(null);
            const result = await authService.verify(credentials);
            if (result.success) {
                setIsAuthenticated(true);
                setError(null);
                // Clear email verification pending status since user successfully verified
                if (emailVerificationUtils.isPending()) {
                    emailVerificationUtils.clear();
                }
                // Try to load user data after successful verification
                try {
                    const userData = await authService.getCurrentUser();
                    setUser(userData);
                    // Identify user in PostHog
                    if (userData.userId) {
                        identifyAnalyticsUser(userData.userId, {
                            email: userData.email,
                        });
                    }
                }
                catch (err) {
                    console.error('Failed to load user data after verification:', err);
                    // Don't throw here - verification was successful, just user data loading failed
                }
            }
            else {
                throw new Error(result.message);
            }
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Verification failed';
            setError(errorMessage);
            throw err; // Re-throw so calling component can handle it
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const logout = useCallback(async () => {
        try {
            await authService.logout();
        }
        catch (error) {
            console.error('Logout failed:', error);
            // Don't set error state since we still want to clear the UI state
        }
        // Reset PostHog identity
        resetAnalyticsUser();
        // Clear any cached authenticated clients
        try {
            const { artifactExportService } = await import('../services/artifactExportService');
            artifactExportService.clearClient();
        }
        catch (error) {
            console.error('Failed to clear artifact export client:', error);
        }
        setIsAuthenticated(false);
        setUser(null);
        setError(null);
        // Wipe the slate clean: a hard reload guarantees every bit of in-memory and
        // Cesium viewer state (selected project, loaded layers, armed tools, drawn
        // markup) is gone. checkAuthStatus on remount finds no tokens and lands the
        // visitor on a fresh, unauthenticated map.
        window.location.reload();
    }, []);
    const checkAuthStatus = useCallback(async () => {
        try {
            const sessionId = localStorage.getItem('landscope_session_id');
            const sessionToken = localStorage.getItem('landscope_session_token');
            if (sessionId && sessionToken) {
                try {
                    // Validate session by trying to get current user
                    const userData = await authService.getCurrentUser();
                    setIsAuthenticated(userData.loginMethod !== 'unverified');
                    setUser(userData);
                }
                catch (err) {
                    console.error('Auth status check failed, clearing tokens:', err);
                    // Set flag to indicate tokens were cleared due to validation failure
                    localStorage.setItem('landscope_session_id_cleared', 'true');
                    // Clear tokens and set unauthenticated state
                    localStorage.removeItem('landscope_session_id');
                    localStorage.removeItem('landscope_session_token');
                    // Clear cached authenticated client
                    authService.clearAuthenticatedClient();
                    // Clear any cached authenticated clients
                    try {
                        const { artifactExportService } = await import('../services/artifactExportService');
                        artifactExportService.clearClient();
                    }
                    catch (error) {
                        console.error('Failed to clear artifact export client:', error);
                    }
                    setIsAuthenticated(false);
                    setUser(null);
                }
            }
            else {
                setIsAuthenticated(false);
                setUser(null);
            }
        }
        catch (err) {
            console.error('Auth status check failed:', err);
            // Check if we had tokens before clearing them
            const hadTokens = localStorage.getItem('landscope_session_id') && localStorage.getItem('landscope_session_token');
            if (hadTokens) {
                localStorage.setItem('landscope_session_id_cleared', 'true');
            }
            // Clear tokens on any error
            localStorage.removeItem('landscope_session_id');
            localStorage.removeItem('landscope_session_token');
            // Clear cached authenticated client
            authService.clearAuthenticatedClient();
            // Clear any cached authenticated clients
            try {
                const { artifactExportService } = await import('../services/artifactExportService');
                artifactExportService.clearClient();
            }
            catch (error) {
                console.error('Failed to clear artifact export client:', error);
            }
            setIsAuthenticated(false);
            setUser(null);
        }
    }, []);
    const getCurrentUser = useCallback(async () => {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        return userData;
    }, []);
    const updateCommentColor = useCallback(async (commentColor) => {
        const result = await authService.updateProfile(commentColor);
        setUser(prev => (prev ? { ...prev, commentColor: result.commentColor || undefined } : prev));
    }, []);
    const uploadAvatar = useCallback(async (image, contentType) => {
        const result = await authService.uploadAvatar(image, contentType);
        setUser(prev => (prev ? { ...prev, avatarUrl: result.avatarUrl || undefined } : prev));
    }, []);
    const removeAvatar = useCallback(async () => {
        await authService.removeAvatar();
        setUser(prev => (prev ? { ...prev, avatarUrl: undefined } : prev));
    }, []);
    const getToken = useCallback(() => {
        return authService.getToken();
    }, []);
    const storeTokens = useCallback((sessionId, sessionToken) => {
        authService.storeTokens(sessionId, sessionToken);
        setIsAuthenticated(true);
        setError(null);
        // Add a delay before attempting to validate the session to ensure it's established server-side
        setTimeout(async () => {
            try {
                const userData = await authService.getCurrentUser();
                setUser(userData);
                // Identify user in PostHog
                if (userData.userId) {
                    identifyAnalyticsUser(userData.userId, {
                        email: userData.email,
                    });
                }
                // After successful authentication, check for pending bundle claim
                // This handles the case where user logged out to switch accounts and is now back
                const pendingClaim = localStorage.getItem('pending_bundle_claim');
                if (pendingClaim) {
                    try {
                        const claimData = JSON.parse(pendingClaim);
                        // Clear from localStorage
                        localStorage.removeItem('pending_bundle_claim');
                        // Redirect to the bundle claim page with the stored parameters
                        // Use window.location to ensure full page navigation after OAuth redirect
                        window.location.href = `/claim-bundle?assignment_id=${claimData.assignment_id}&user_id=${claimData.user_id}&csrf=${claimData.csrf}`;
                    }
                    catch (e) {
                        console.error('Failed to parse pending bundle claim:', e);
                        localStorage.removeItem('pending_bundle_claim');
                    }
                }
            }
            catch (err) {
                console.error('AuthContext: Failed to load user data after token storage:', err);
                // If this fails, don't clear the tokens immediately - let the user try to use the app
            }
        }, 2000);
    }, []);
    const value = useMemo(() => ({
        isAuthenticated,
        isLoading,
        error,
        user,
        login,
        verify,
        logout,
        checkAuthStatus,
        getCurrentUser,
        updateCommentColor,
        uploadAvatar,
        removeAvatar,
        getToken,
        storeTokens
    }), [
        isAuthenticated,
        isLoading,
        error,
        user,
        login,
        verify,
        logout,
        checkAuthStatus,
        getCurrentUser,
        updateCommentColor,
        uploadAvatar,
        removeAvatar,
        getToken,
        storeTokens
    ]);
    return (_jsx(AuthContext.Provider, { value: value, children: children }));
};
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
